import Analytics from '@/utils/analytics';
import React, { useEffect, useMemo, useRef } from 'react';
import { CategoryListOptionMetadata } from '@/utils/category';
import { createStyles } from '@/contexts/theme-context';
import { FilterButton, FilterType } from './filter-button';
import {
  FlatList,
  InteractionManager,
  PixelRatio,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { HEADER_PADDING_HORIZONTAL_SMALL, SCREEN_PADDING } from '@/styles';
import { HorizontalListArrows } from './horizontal-list-arrows';
import { ListOption } from './list-item';
import {
  useLayoutContext,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';

export const LIST_HEIGHT = 40 * PixelRatio.getFontScale();

interface Props<T> {
  allFilter?: T;
  containerStyle?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  data: T[];
  deselectAllFilter?: boolean;
  filterType?: FilterType;
  hideFooterSpacing?: boolean;
  keyExtractor: (item: T) => string;
  onItemPress: (item: T | null) => void;
  scrollToItem?: boolean;
  selectedItem: T | null;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  trackingKey: string;
}

type PossibleTypes = string | ListOption;

export const HorizontalSelector = <T extends PossibleTypes>({
  allFilter,
  containerStyle,
  contentContainerStyle,
  data,
  deselectAllFilter = true,
  filterType,
  keyExtractor,
  onItemPress,
  selectedItem,
  style,
  trackingKey,
  scrollToItem,
  hideFooterSpacing,
  testID,
}: Props<T>) => {
  const [styles] = useStyles();

  const listData = useMemo(() => {
    if (!allFilter) {
      return data;
    }
    return [allFilter, ...data];
  }, [allFilter, data]);

  const { isSmallLayout } = useLayoutContext();

  const listContentContainerStyle = useSmallLayoutSizeValue<
    StyleProp<ViewStyle>
  >(
    [styles.listContentContainer, contentContainerStyle],
    [
      styles.listContentContainer,
      styles.listContentContainerLarge,
      contentContainerStyle,
    ],
  );

  const listRef = useRef<FlatList<T> | null>(null);

  const initialScrollIndex = useMemo(() => {
    if (scrollToItem && selectedItem) {
      const index = listData.findIndex((item) => {
        if (typeof item === 'string') {
          return item === selectedItem;
        }
        return (
          typeof selectedItem !== 'string' &&
          item.value === String(selectedItem.value)
        );
      });
      if (index > (Boolean(allFilter) ? 0 : -1)) {
        return index;
      }
    }
  }, [allFilter, listData, scrollToItem, selectedItem]);

  useEffect(() => {
    if (typeof initialScrollIndex === 'number') {
      InteractionManager.runAfterInteractions(() => {
        listRef.current?.scrollToIndex({
          animated: true,
          index: initialScrollIndex,
        });
      });
    }
  }, [initialScrollIndex]);

  return data.length > 0 ? (
    <View style={[styles.relativeContainer, containerStyle]} testID={testID}>
      <HorizontalListArrows<T> trackingKey={trackingKey}>
        {(listProps, arrowListRef) => (
          <FlatList<T>
            {...listProps}
            contentContainerStyle={listContentContainerStyle}
            data={listData}
            horizontal
            initialNumToRender={
              typeof initialScrollIndex === 'number'
                ? initialScrollIndex + 1
                : undefined
            }
            keyExtractor={keyExtractor}
            ListFooterComponent={
              isSmallLayout && !hideFooterSpacing ? (
                <View style={styles.filterListFooter} />
              ) : null
            }
            onScrollToIndexFailed={() => {
              // prevents errors if scrolling before being measured
            }}
            ref={(ref) => {
              // attach both local and arrow list refs
              listRef.current = ref;
              arrowListRef.current = ref;
            }}
            renderItem={({ index, item }) => {
              const isSelected = selectedItem
                ? typeof selectedItem === 'string'
                  ? selectedItem === item
                  : // @ts-ignore both will be the same type
                    selectedItem.value === item.value
                : allFilter && index === 0;
              return (
                <FilterButton
                  count={
                    typeof item !== 'string'
                      ? (item.metadata as CategoryListOptionMetadata)?.count
                      : undefined
                  }
                  filterType={filterType}
                  isSelected={isSelected}
                  onPress={() => {
                    Analytics.track(`${trackingKey}.button.press`, {
                      item: typeof item === 'string' ? item : item.value,
                    });

                    // if allFilter is passed and first item is 0, deselect
                    onItemPress(
                      (deselectAllFilter && allFilter && index === 0) ||
                        isSelected
                        ? null
                        : item,
                    );
                  }}
                  text={typeof item === 'string' ? item : item.title}
                />
              );
            }}
            showsHorizontalScrollIndicator={false}
            style={[styles.base, style]}
          />
        )}
      </HorizontalListArrows>
    </View>
  ) : null;
};

const useStyles = createStyles((theme) => ({
  base: {
    backgroundColor: theme.colors.white,
  },
  filterListFooter: {
    width: SCREEN_PADDING,
  },
  listContentContainer: {
    height: LIST_HEIGHT,
    marginLeft: HEADER_PADDING_HORIZONTAL_SMALL,
  },
  listContentContainerLarge: {
    marginLeft: 0,
  },
  relativeContainer: {
    flex: 1,
  },
}));
